import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import {
  ChevronRight,
  Clock,
  User,
  ArrowRight,
  ArrowRightLeft,
  Mail,
  FileText,
  CreditCard,
  CircleDot,
  Pencil,
  ShieldCheck,
} from "lucide-react";

interface UnifiedActivityFeedProps {
  leadId: string;
}

interface FeedEntry {
  id: string;
  timestamp: string;
  kind: "activity" | "change" | "purge";
  title: string;
  details?: string | null;
  userName?: string | null;
  field?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
  reason?: string | null;
}

const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  const statusMap: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    lead: "outline",
    quote_generated: "secondary",
    quote_sent: "default",
    quote_accepted: "default",
    executing: "secondary",
    completed: "default",
    cancelled: "destructive",
    aborted: "destructive",
  };
  return statusMap[status] || "outline";
};

const formatStatusLabel = (status: string): string =>
  status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getActivityIcon = (action: string) => {
  const a = action.toLowerCase();
  if (a.includes("email") || a.includes("mail")) return Mail;
  if (a.includes("quote") || a.includes("programme")) return FileText;
  if (a.includes("invoice") || a.includes("payment")) return CreditCard;
  return CircleDot;
};

export const UnifiedActivityFeed = ({ leadId }: UnifiedActivityFeedProps) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  const { data: entries, isLoading } = useQuery({
    queryKey: ["unified-activity-feed", leadId],
    queryFn: async () => {
      const [activitiesRes, changesRes, purgeRes] = await Promise.all([
        supabase
          .from("activity_log")
          .select("*")
          .eq("lead_id", leadId)
          .order("created_at", { ascending: false }),
        supabase
          .from("lead_changes")
          .select("*, profiles:user_id(full_name, email)")
          .eq("lead_id", leadId)
          .order("created_at", { ascending: false }),
        supabase
          .from("sensitive_doc_purge_log")
          .select("*")
          .eq("lead_id", leadId)
          .order("created_at", { ascending: false }),
      ]);

      if (activitiesRes.error) throw activitiesRes.error;
      if (changesRes.error) throw changesRes.error;

      const activities = activitiesRes.data || [];
      const changes = changesRes.data || [];
      // Purge audit is staff-only; never let it break the rest of the feed.
      const purges = purgeRes.error ? [] : purgeRes.data || [];

      // Resolve profile names for activity and purge authors (no reliable join)
      const activityUserIds = [
        ...new Set(
          [...activities.map((a: any) => a.user_id), ...purges.map((p: any) => p.purged_by)].filter(
            Boolean
          )
        ),
      ];
      const { data: activityProfiles } = activityUserIds.length
        ? await supabase.from("profiles").select("id, full_name, email").in("id", activityUserIds)
        : { data: [] as any[] };
      const profileMap = new Map((activityProfiles || []).map((p: any) => [p.id, p]));

      const purgeEntries: FeedEntry[] = purges.map((p: any) => {
        const parts = [
          `${p.submissions_count} intake submission${p.submissions_count === 1 ? "" : "s"} cleared of ID/passport details`,
          `${p.files_count} ID/passport file${p.files_count === 1 ? "" : "s"} permanently deleted`,
          `${p.vault_docs_count} Documents cop${p.vault_docs_count === 1 ? "y" : "ies"} and share links removed`,
        ];
        return {
          id: `purge-${p.id}`,
          timestamp: p.created_at,
          kind: "purge",
          title: p.automatic
            ? "ID documents purged automatically"
            : "ID documents purged",
          details: parts.join(" · "),
          reason: p.automatic ? "14-day retention period ended" : "Purged early by an admin",
          userName: p.automatic
            ? "Automatic nightly purge"
            : profileMap.get(p.purged_by)?.full_name || profileMap.get(p.purged_by)?.email || null,
        };
      });

      const changeEntries: FeedEntry[] = changes.map((c: any) => ({
        id: `change-${c.id}`,
        timestamp: c.created_at || c.change_date,
        kind: "change",
        title:
          c.field_changed === "status"
            ? `Status: ${formatStatusLabel(c.old_value || "?")} → ${formatStatusLabel(c.new_value || "?")}`
            : `${c.field_changed} updated`,
        userName: c.profiles?.full_name || c.profiles?.email || null,
        field: c.field_changed,
        oldValue: c.old_value,
        newValue: c.new_value,
        reason: c.change_source,
      }));

      // Dedupe: status changes are double-written to activity_log ("Status Changed" /
      // "Lead Cancelled") and lead_changes. Keep the richer lead_changes row.
      const isDuplicateOfChange = (a: any) => {
        if (a.action !== "Status Changed" && a.action !== "Lead Cancelled") return false;
        const at = new Date(a.created_at).getTime();
        return changes.some((c: any) => {
          if (c.field_changed !== "status") return false;
          const ct = new Date(c.created_at || c.change_date).getTime();
          return Math.abs(at - ct) < 5 * 60 * 1000;
        });
      };

      const activityEntries: FeedEntry[] = activities
        .filter((a: any) => !isDuplicateOfChange(a))
        .map((a: any) => ({
          id: `activity-${a.id}`,
          timestamp: a.created_at,
          kind: "activity",
          title: a.action,
          details: a.details,
          userName: a.user_id
            ? profileMap.get(a.user_id)?.full_name || profileMap.get(a.user_id)?.email || null
            : null,
        }));

      return [...changeEntries, ...activityEntries, ...purgeEntries].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    },
  });

  // Expand the two newest entries by default once data first arrives
  useMemo(() => {
    if (!initialized && entries && entries.length > 0) {
      setExpandedIds(new Set(entries.slice(0, 2).map((e) => e.id)));
      setInitialized(true);
    }
  }, [entries, initialized]);

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedIds(new Set((entries || []).map((e) => e.id)));
  const collapseAll = () => setExpandedIds(new Set());

  const hasExpandableContent = (entry: FeedEntry) =>
    !!(entry.details || entry.reason || entry.field || entry.userName);

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Activity Feed</h2>
        <p className="text-sm text-muted-foreground">Loading activity...</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">
          Activity Feed
          {entries && entries.length > 0 && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({entries.length})
            </span>
          )}
        </h2>
        {entries && entries.length > 0 && (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={expandAll}>
              Expand all
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={collapseAll}>
              Collapse all
            </Button>
          </div>
        )}
      </div>

      {!entries || entries.length === 0 ? (
        <p className="text-muted-foreground text-center py-4 text-sm">No activity yet</p>
      ) : (
        <div className="space-y-1">
            {entries.map((entry) => {
              const isOpen = expandedIds.has(entry.id);
              const Icon =
                entry.kind === "purge"
                  ? ShieldCheck
                  : entry.kind === "change"
                    ? entry.field === "status"
                      ? ArrowRightLeft
                      : Pencil
                    : getActivityIcon(entry.title);
              const expandable = hasExpandableContent(entry);

              return (
                <Collapsible key={entry.id} open={isOpen} onOpenChange={() => toggle(entry.id)}>
                  <CollapsibleTrigger
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-muted/60 transition-colors"
                    disabled={!expandable}
                  >
                    <ChevronRight
                      className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                        isOpen ? "rotate-90" : ""
                      } ${!expandable ? "invisible" : ""}`}
                    />
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 text-sm font-medium truncate">{entry.title}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                      <Clock className="h-3 w-3" />
                      {format(new Date(entry.timestamp), "MMM d, h:mm a")}
                    </span>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="ml-8 mr-2 mb-2 space-y-2 rounded-md bg-muted/40 p-3">
                      {entry.kind === "change" && entry.field === "status" && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={getStatusBadgeVariant(entry.oldValue || "")}>
                            {formatStatusLabel(entry.oldValue || "?")}
                          </Badge>
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          <Badge variant={getStatusBadgeVariant(entry.newValue || "")}>
                            {formatStatusLabel(entry.newValue || "?")}
                          </Badge>
                        </div>
                      )}

                      {entry.kind === "change" && entry.field !== "status" && (
                        <div className="text-sm text-muted-foreground">
                          <span className="line-through">{entry.oldValue || "(empty)"}</span>
                          {" → "}
                          <span className="font-medium text-foreground">
                            {entry.newValue || "(empty)"}
                          </span>
                        </div>
                      )}

                      {entry.details && (
                        <p className="text-sm text-muted-foreground">{entry.details}</p>
                      )}

                      {entry.reason && (
                        <p className="text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">Reason: </span>
                          {entry.reason}
                        </p>
                      )}

                      {entry.userName && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <User className="h-3 w-3" />
                          <span>{entry.userName}</span>
                          <span>·</span>
                          <span>
                            {format(new Date(entry.timestamp), "MMM d, yyyy 'at' h:mm a")}
                          </span>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
        </div>
      )}
    </Card>
  );
};
